import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput.tsx';
import ImageInput from 'components/forms/inputs/rhf-inputs/ImageInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import PhoneNumberInput from 'components/forms/inputs/rhf-inputs/PhoneNumberInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import SignatureIcon from 'components/icons/SignatureIcon';
import InfoTooltip from 'components/InfoTooltip';
import { PersonalCodeShowLink, PersonalCodeValue } from 'components/PersonalCodeShow';
import { useInSectionEditForm } from 'components/section-edit/SectionEdit';
import { FieldError } from 'components/ui/field';
import TargetsFields, { TargetsForm } from 'pages/dashboard/progress-tracking/TargetsFields';
import { flushSync } from 'react-dom';
import { Control, useWatch } from 'react-hook-form';
import { useLazyGetEmployeePersonalIdCodeQuery, useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ListedIn } from 'types/back-office-settings';
import { FeatureFlag } from 'types/features';
import { SystemRole } from 'types/systemRoles';
import { errorAlert } from 'utils/alert';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { formatEmployeeName } from 'utils/nameOrder';
import { isPersonalCodeMask } from 'utils/personalCodeMask';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import AddAffiliationButton, { Position as AffiliationPosition } from '../affiliations/AddAffiliationButton';
import AffiliationsHistory from '../affiliations/AffiliationsHistory';
import AddEmploymentButton, { Position } from '../employment/AddEmploymentButton';
import { EmploymentHistory } from '../employment/EmploymentHistory';
import { WebPictureInput } from '../views/WebPictureInput';
import { EmployeeFormState } from './useEmployeeForm';

type Props = { form: EmployeeFormState };

// The edit page's General section interleaves four cards' fields; it composes these pieces in its own order.

/** Identity: the pictures, the names in the tenant's order, the title. */
export const PictureAndNameFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const { lastNameFirst, personTitleOptions, profilePictureUploadRef } = form;
    const inSection = useInSectionEditForm();

    const firstNameFields = (
        <>
            <TextInput
                control={control}
                label="first-name"
                placeholder="employees.enter-first-name"
                name="firstName"
                required
                maxLength={255}
            />
            <FeatureFlagged action="show" flag={FeatureFlag.LOCALIZED_FIELDS}>
                <TextInput
                    control={control}
                    label="first-name-local"
                    placeholder="employees.enter-first-name-local"
                    name="localFirstName"
                    maxLength={255}
                />
            </FeatureFlagged>
        </>
    );

    const lastNameFields = (
        <>
            <TextInput
                control={control}
                label="last-name"
                placeholder="employees.enter-last-name"
                name="lastName"
                required
                maxLength={255}
            />
            <FeatureFlagged action="show" flag={FeatureFlag.LOCALIZED_FIELDS}>
                <TextInput
                    control={control}
                    label="last-name-local"
                    placeholder="employees.enter-last-name-local"
                    name="localLastName"
                    maxLength={255}
                />
            </FeatureFlagged>
        </>
    );

    const pictures = (
        <>
            <ImageInput
                control={control}
                label="employees.profile-picture"
                imageHoverText={t('employees.edit-profile-picture')}
                noImageHoverText={t('employees.add-profile-picture')}
                removeImageHoverText={t('employees.remove-profile-picture')}
                name="profilePictureName"
                setValue={setValue}
                uploadEndpoint={'/v1/employees/profile-pictures'}
                downloadEndpoint={'/v1/employees/profile-pictures/'}
                onError={() => errorAlert(t('employees.error-uploading-profile-picture'))}
                enableCrop
                pendingUploadRef={profilePictureUploadRef}
            />
            <FeatureFlagged action="show" flag={FeatureFlag.EMPLOYEE_SIGNATURE_FILE}>
                <ImageInput
                    control={control}
                    label="employees.signature-picture"
                    imageHoverText={t('employees.edit-signature-picture')}
                    noImageHoverText={t('employees.add-signature-picture')}
                    removeImageHoverText={t('employees.remove-signature-picture')}
                    name="signaturePictureName"
                    setValue={setValue}
                    uploadEndpoint={'/v1/employees/signature-picture'}
                    downloadEndpoint={'/v1/employees/signature-picture/'}
                    onError={() => errorAlert(t('employees.error-uploading-signature-picture'))}
                    accept={'.png'}
                    maxWidth={750}
                    maxHeight={350}
                    variant={'square'}
                    width={300}
                    height={150}
                    fallbackIcon={<SignatureIcon className="text-[60px]" />}
                />
            </FeatureFlagged>
        </>
    );

    return (
        <>
            {/* A section's grid would squeeze each picture into a cell; side by side they take the row. The create
                dialog draws each field inside two boxless wrappers, so the width goes by the field's own class. */}
            {inSection ? <div className="flex flex-wrap gap-x-5 [&_.form-field]:w-auto">{pictures}</div> : pictures}
            {lastNameFirst ? (
                <>
                    {lastNameFields}
                    {firstNameFields}
                </>
            ) : (
                <>
                    {firstNameFields}
                    {lastNameFields}
                </>
            )}
            <SelectInput
                setValue={setValue}
                control={control}
                label="title"
                placeholder="title"
                name="personTitle"
                options={personTitleOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
            />
        </>
    );
};

export const PreviousNamesField = ({ form }: Props): JSX.Element => (
    <TextAreaInput
        name="previousNames"
        control={form.methods.control}
        label="employees.previous-names"
        placeholder="employees.previous-names"
    />
);

/** Account & access: active, and the system roles the user may see, with the administrative ones locked. */
export const ActiveAndRolesFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        type,
        isActive,
        systemRoles,
        isEditingCurrentUserAndHasPermissionToUpdateEmployees,
        hasReadRolePermission,
        hasUpdateRolesPermission,
        canSeeAllRoles,
        canUpdateAdministrativeRoles,
    } = form;
    return (
        <>
            <CheckboxInput
                control={control}
                name="isActive"
                label="active"
                readonly={isEditingCurrentUserAndHasPermissionToUpdateEmployees}
            />
            {hasReadRolePermission ? (
                <MultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="employees.system-roles"
                    placeholder="employees.select-system-roles"
                    name="systemRoles"
                    options={systemRoles ?? []}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    required={isActive && (canSeeAllRoles || type === 'add')}
                    readonly={!hasUpdateRolesPermission}
                    getOptionFixed={
                        // Lock administrative roles unless the user can update them
                        canSeeAllRoles && hasUpdateRolesPermission && !canUpdateAdministrativeRoles
                            ? (role: SystemRole) => {
                                  const roleFromOptions = systemRoles?.find((r) => r.id === role.id);
                                  return roleFromOptions?.administrativeRole === true;
                              }
                            : undefined
                    }
                />
            ) : null}
        </>
    );
};

export const CanImpersonateField = ({ form }: Props): JSX.Element | null =>
    form.canEditImpersonation ? (
        <DynamicMultiSelectInput
            setValue={form.methods.setValue}
            control={form.methods.control}
            label="employees.can-impersonate"
            placeholder="employees.select-can-impersonate"
            name="canImpersonate"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
        />
    ) : null;

export const ContactDetailsFields = ({ form }: Props): JSX.Element => {
    const { control } = form.methods;
    const { validEmail, emailIsNotUsed } = form;
    return (
        <>
            <TextInput
                control={control}
                label="employees.work-email-address"
                placeholder="employees.enter-email"
                name="workEmailAddress"
                maxLength={255}
                validate={{ validEmail: validEmail, emailIsNotUsed: emailIsNotUsed }}
            />
            <PhoneNumberInput
                control={control}
                label="employees.mobile-phone"
                placeholder="employees.enter-mobile-phone-number"
                name="mobilePhone"
                maxLength={255}
            />
            <PhoneNumberInput
                control={control}
                label="employees.work-phone"
                placeholder="employees.enter-work-phone-number"
                name="workPhone"
                maxLength={255}
            />
        </>
    );
};

/** Employment: supervisor, invoicing identity, dates, notes. */
export const EmploymentDetailsFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="supervisor"
                placeholder="employees.select-supervisor"
                name="manager"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.EMPLOYEE_SUPERVISOR } },
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <FeatureFlagged action="show" flag={FeatureFlag.EMPLOYEE_VAT_REG_NUM}>
                <TextInput
                    control={control}
                    label="employees.employee-vat-reg-num"
                    placeholder="employees.enter-employee-vat-reg-num"
                    name="vatRegNum"
                    maxLength={255}
                />
            </FeatureFlagged>
            <FeatureFlagged action="show" flag={FeatureFlag.SIGNED_BY_ON_INVOICE_INSTEAD}>
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="employees.overwrite-signed-by-employee-id"
                    placeholder="employees.enter-overwrite-signed-by-employee-id"
                    name="overwriteSignedByEmployeeId"
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                />
            </FeatureFlagged>
            <DateInput control={control} name="employmentStartDate" label="employees.employment-start-date" required />
            <DateInput control={control} name="employmentLeaveDate" label="employees.employment-end-date" />
            <TextAreaInput
                name="notes"
                control={control}
                label="employees.notes"
                placeholder="employees.notes"
                maxLength={255}
                enableAutofill
            />
        </>
    );
};

export const TaskReceiversField = ({ form }: Props): JSX.Element => (
    <DynamicMultiSelectInput
        setValue={form.methods.setValue}
        control={form.methods.control}
        label="employees.tasks-shared-with"
        placeholder="employees.select-tasks-shared-with"
        name="taskReceivers"
        paginated={{
            fetch: useLazyGetEmployeesListWithFiltersQuery,
            searchField: 'fullName',
        }}
        getOptionLabel="name"
        getOptionIdentifier="id"
    />
);

export const EmploymentHistoryFields = ({ form }: Props): JSX.Element => {
    const {
        control,
        setValue,
        formState: { errors },
    } = form.methods;
    const { jobRoleNamedEntities, jobPositionNamedEntities, modalActions } = form;
    const inCreateDialog = useInCreateDialog();
    const employmentHistory = useWatch({ control, name: 'employmentHistory' });
    const hasError = errors?.employmentHistory != null;

    const rows = (
        <>
            <EmploymentHistory
                name="employmentHistory"
                setValue={setValue}
                control={control}
                readonly={false}
                jobRoleList={jobRoleNamedEntities ?? []}
                jobPositionList={jobPositionNamedEntities ?? []}
                modalActions={modalActions}
            />
            <AddEmploymentButton
                position={Position.SECTION_SIDE}
                control={control}
                setValue={setValue}
                jobRoleList={jobRoleNamedEntities}
                jobPositionList={jobPositionNamedEntities}
                modalActions={modalActions}
            />
            {hasError ? (
                <FieldError className="pl-4 text-xs">{errors.employmentHistory?.message || 'error'}</FieldError>
            ) : null}
        </>
    );

    // An employee is not saved without an employment, so the create dialog draws the rows in Required. They are
    // no single input: there they are one block, which counts as one field, left to fill until it has a row. It is
    // marked invalid while the form holds their error, which is how a refused Create finds them.
    return (
        <CreateDialogRequiredBlock when isEmpty={!employmentHistory?.length} hasErrors={hasError}>
            {inCreateDialog ? (
                <div className="flex flex-col gap-3 *:m-0!" data-invalid={hasError || undefined}>
                    {rows}
                </div>
            ) : (
                rows
            )}
        </CreateDialogRequiredBlock>
    );
};

/** Competence: teams, practice areas, languages. */
export const CompetenceFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const { practiceAreaList, classifiers } = form;
    return (
        <>
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="employees.support-teams"
                placeholder="employees.select-support-team"
                name="supportTeams"
                paginated={{
                    fetch: useLazyGetMinTeamsListWithFiltersQuery,
                    searchField: 'name',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="employees.chairman-teams"
                placeholder="employees.select-chairman-team"
                name="chairmanTeams"
                paginated={{
                    fetch: useLazyGetMinTeamsListWithFiltersQuery,
                    searchField: 'name',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="practice-areas.practice-areas"
                placeholder="timecards.form.select-practice-areas"
                name="practiceAreas"
                options={practiceAreaList ?? []}
                getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                getOptionIdentifier="id"
            />
            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="employees.languages-spoken"
                placeholder="employees.select-languages"
                name="languagesSpoken"
                options={getValidOptionsFromClassifier<string, string>(classifiers['LANGUAGE']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
            />
        </>
    );
};

export const PeerGroupField = ({ form }: Props): JSX.Element | null =>
    form.canReadPartnerPeerGroup ? (
        <FeatureFlagged action="show" flag={FeatureFlag.PARTNER_PEER_GROUP}>
            <SelectInput
                setValue={form.methods.setValue}
                control={form.methods.control}
                label="employees.partnership-peer-group"
                placeholder="employees.select-partnership-peer-group"
                name="partnershipPeerGroup"
                options={form.partnershipPeerGroupOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
                readonly={!form.hasUpdatePartnerPeerGroupPermission}
                tooltip={<InfoTooltip text="employees.partnership-peer-group-tooltip" />}
            />
        </FeatureFlagged>
    ) : null;

/** Web integration: the publication flags and the website picture, the record's fields the website receives. */
export const WebIntegrationFields = ({ form }: Props): JSX.Element => {
    const { control } = form.methods;
    const [firstName, lastName] = useWatch({ control, name: ['firstName', 'lastName'] });
    return (
        <>
            <WebPictureInput
                control={control}
                fullName={formatEmployeeName({ firstName, lastName }, form.lastNameFirst)}
            />
            <CheckboxInput control={control} name="web.publicizePerson" label="employees.web.publicize-person" />
            <CheckboxInput control={control} name="web.publicizeMobile" label="employees.web.publicize-mobile" />
            <CheckboxInput control={control} name="web.onLeave" label="employees.web.on-leave" />
        </>
    );
};

export const AffiliationsFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <AffiliationsHistory
                name="affiliations"
                setValue={setValue}
                control={control}
                readonly={false}
                modalActions={form.modalActions}
            />
            <AddAffiliationButton
                position={AffiliationPosition.SECTION_SIDE}
                control={control}
                setValue={setValue}
                modalActions={form.modalActions}
            />
        </>
    );
};

/** Personal: what the user may read of it, editable with UPDATE.EMPLOYEE_PERSONAL.ALL. */
export const PersonalFields = ({ form }: Props): JSX.Element => {
    const { control, setValue, setFocus } = form.methods;
    const {
        canReadAllPersonalData,
        canUpdatePersonalData,
        canShowPersonalIdCode,
        employeeId,
        genderOptions,
        classifiers,
    } = form;
    const [getPersonalIdCode] = useLazyGetEmployeePersonalIdCodeQuery();
    const personalIdCode = useWatch({ control, name: 'personalIdCode' });
    const requestPersonalIdCode =
        employeeId && canShowPersonalIdCode && isPersonalCodeMask(personalIdCode)
            ? () => getPersonalIdCode(employeeId)
            : undefined;
    return (
        <>
            {canReadAllPersonalData ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="employees.gender"
                    placeholder="employees.choose-gender"
                    name="genderType"
                    options={genderOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    readonly={!canUpdatePersonalData}
                />
            ) : null}
            <TextInput
                control={control}
                label="employees.personal-id-code"
                placeholder="employees.enter-personal-id-code"
                name="personalIdCode"
                readonly={!canUpdatePersonalData}
                readOnlyField={
                    requestPersonalIdCode ? (
                        <span className="text-fg text-sm">
                            <PersonalCodeValue code={personalIdCode} canShow request={requestPersonalIdCode} />
                        </span>
                    ) : undefined
                }
                maxLength={255}
                suffixComponent={
                    requestPersonalIdCode ? (
                        <PersonalCodeShowLink
                            request={requestPersonalIdCode}
                            onShow={(code) => {
                                // The link goes with the mask: the field is drawn without it before it takes focus.
                                flushSync(() => setValue('personalIdCode', code));
                                setFocus('personalIdCode');
                            }}
                        />
                    ) : undefined
                }
            />
            {canReadAllPersonalData ? (
                <>
                    <DateInput
                        control={control}
                        name="birthdayDate"
                        label="employees.date-of-birth"
                        readonly={!canUpdatePersonalData}
                    />
                    <TextAreaInput
                        control={control}
                        name="sensitiveInfo"
                        label="employees.sensitive-info"
                        placeholder="employees.enter-sensitive-info"
                        enableAutofill
                        tooltip={<InfoTooltip text="employees.sensitive-info-tooltip" />}
                        readonly={!canUpdatePersonalData}
                        maxLength={1000}
                    />
                    <CheckboxInput
                        control={control}
                        name="isPoliticallyExposedPerson"
                        label="employees.politically-exposed-person"
                        readonly={!canUpdatePersonalData}
                    />
                    <AddressInput
                        label="employees.personal-address"
                        tooltip={<InfoTooltip text="employees.personal-address-tooltip" />}
                        name="personalAddress"
                        control={control}
                        classifiers={classifiers}
                        hasPrivilege={canUpdatePersonalData}
                    />
                </>
            ) : null}
        </>
    );
};

export const EmployeeTargetsFields = ({ form }: Props): JSX.Element => (
    <TargetsFields
        control={form.methods.control as unknown as Control<TargetsForm>}
        readonly={!form.canUpdatePersonalData}
        storedPlannedWorkdaysOff={form.initialData?.plannedWorkdaysOff}
    />
);
